import {BookizonAppManager} from "../index";
import FuxEvents from "../lib/FuxFramework/FuxEvents";
import {NM_GEOLOCATION_PERMISSION, NM_GEOLOCATION_UPDATE} from "../native/NativeMessageHandler";
import {UserAPI} from "../api/User/UserAPI";
import AuthAPI from "../api/AuthAPI";
import Store from "../components/Store";

// Existing positional arguments remain valid. Override these defaults or pass a final options object.
export const GEOLOCATION_DEFAULTS = {
    timeoutMs: 30000,
    permissionTimeoutMs: 60000,
    permissionCheckTimeoutMs: 3000,
    permissionPollIntervalMs: 1000,
    maximumAgeMs: 60000,
    cacheMaxAgeMs: 60000,
    legacyFallbackDelayMs: 1000,
    nativeTimeoutMs: 120000
};
const ERROR_EVENT = 'geolocation.error';
const ownerRequests = new Map();
let activePosition = null;
let sequence = 0;

function settings(options = {}) {
    const result = {...GEOLOCATION_DEFAULTS, ...options};
    Object.keys(GEOLOCATION_DEFAULTS).forEach(key => {
        const value = result[key];
        const minimum = key === 'maximumAgeMs' || key === 'cacheMaxAgeMs' ? 0 : 1;
        result[key] = typeof value === 'number' && Number.isFinite(value) && value >= minimum
            ? Math.min(value, 120000) : GEOLOCATION_DEFAULTS[key];
    });
    return result;
}

function owned(promise, owner) {
    if (!owner) return promise;
    if (!ownerRequests.has(owner)) ownerRequests.set(owner, new Set());
    const requests = ownerRequests.get(owner);
    requests.add(promise);
    const cleanup = () => {
        requests.delete(promise);
        if (!requests.size && ownerRequests.get(owner) === requests) ownerRequests.delete(owner);
    };
    promise.then(cleanup, cleanup);
    return promise;
}

// Register cleanup before invoking a bridge, since simulators can respond synchronously.
function wait(setup, cancelledValue, owner) {
    let resolvePromise;
    let settled = false;
    const cleanups = [];
    const promise = new Promise(resolve => {
        resolvePromise = resolve;
    });
    const finish = value => {
        if (settled) return;
        settled = true;
        cleanups.reverse().forEach(cleanup => {
            try {
                cleanup();
            } catch (error) { /* A bridge failure must not leave the Promise pending. */
            }
        });
        resolvePromise(value);
    };
    const cleanup = fn => settled ? fn() : cleanups.push(fn);
    promise.cancel = () => finish(cancelledValue);
    try {
        setup(finish, cleanup);
    } catch (error) {
        finish(cancelledValue);
    }
    return owned(promise, owner);
}

function normalizeLocation(data) {
    if (!data || typeof data.lat !== 'number' || typeof data.lon !== 'number'
        || !Number.isFinite(data.lat) || !Number.isFinite(data.lon)
        || Math.abs(data.lat) > 90 || Math.abs(data.lon) > 180) return null;
    return {
        lat: data.lat, lng: data.lon,
        accuracy: typeof data.accuracy === 'number' && Number.isFinite(data.accuracy) && data.accuracy >= 0 ? data.accuracy : null,
        timestamp: typeof data.timestamp === 'number' && Number.isFinite(data.timestamp) && data.timestamp > 0 ? data.timestamp : null,
        receivedAt: typeof data.receivedAt === 'number' && Number.isFinite(data.receivedAt) && data.receivedAt > 0 ? data.receivedAt : null
    };
}

function fresh(timestamp, maxAgeMs) {
    const age = Date.now() - timestamp;
    return timestamp !== null && age >= 0 && age <= maxAgeMs;
}

// The existing message handler keeps one in-memory sample, including events received during consent.
// Never promote reception time to measurement time or reload undated samples from persistent storage.
function bufferedLegacyLocation(options) {
    const native = Store.state.native;
    if (!native || native.locationRequestId != null) return null;
    const location = normalizeLocation({...native, lon: native.lng});
    if (!location || !fresh(location.receivedAt, options.cacheMaxAgeMs)) return null;
    if (location.timestamp !== null && !fresh(location.timestamp, Math.min(options.cacheMaxAgeMs, options.maximumAgeMs))) return null;
    return {...location, fromCache: true};
}

function cachedLocation(options, allowLegacyBuffer = false) {
    if (allowLegacyBuffer) {
        const buffered = bufferedLegacyLocation(options);
        if (buffered) return buffered;
    }
    try {
        const data = JSON.parse(localStorage.getItem('__last_geolocation'));
        const location = normalizeLocation(data);
        return location && fresh(location.timestamp, options.cacheMaxAgeMs) ? {...location, fromCache: true} : null;
    } catch (error) {
        return null;
    }
}

function saveLocation(location) {
    try {
        localStorage.__last_lat = location.lat;
        localStorage.__last_lng = location.lng;
        // A legacy result without a measurement timestamp must not become a fresh cache entry.
        localStorage.setItem('__last_geolocation', JSON.stringify({...location, lon: location.lng}));
    } catch (error) {
        // Storage can be unavailable; the position request must still succeed.
    }
    if (AuthAPI.currentUser) {
        try {
            Promise.resolve(UserAPI.Geolocation.save(location.lat, location.lng)).catch(() => {
            });
        } catch (error) {
            // Saving is best effort and must not retain the position listeners.
        }
    }
}

function stopPosition(request) {
    if (activePosition !== request) return;
    activePosition = null;
    FuxEvents.off(NM_GEOLOCATION_UPDATE, request.onLocation);
    FuxEvents.off(ERROR_EVENT, request.onError);
    if (request.modern) BookizonAppManager.cancelGeolocation(request.id);
}

function newPositionRequest() {
    const request = {
        id: 'location-' + Date.now() + '-' + (++sequence),
        modern: BookizonAppManager.supportsSingleGeolocation(),
        subscribers: new Set()
    };
    const matches = data => request.modern ? data.requestId === request.id : !data.requestId;
    request.onLocation = data => {
        if (activePosition !== request || !matches(data)) return;
        const location = normalizeLocation(data);
        if (!location) return;
        const subscribers = [...request.subscribers].filter(subscriber =>
            // Old apps sometimes omit metadata: accept the fix, but don't invent accuracy or age.
            location.timestamp === null || fresh(location.timestamp, subscriber.options.maximumAgeMs));
        if (!subscribers.length) return;
        saveLocation(location);
        subscribers.forEach(subscriber => subscriber.finish(location));
    };
    request.onError = data => {
        if (activePosition !== request || !matches(data)) return;
        // Never use a cached position after explicit cancellation or permission denial.
        const allowCache = data.code !== 'CANCELLED' && data.code !== 'PERMISSION_DENIED';
        [...request.subscribers].forEach(subscriber => subscriber.finish(
            allowCache && subscriber.fallback ? cachedLocation(subscriber.options, !request.modern) : null));
    };
    activePosition = request;
    FuxEvents.on(NM_GEOLOCATION_UPDATE, request.onLocation);
    FuxEvents.on(ERROR_EVENT, request.onError);
    return request;
}

const NativeGeolocationProvider = {
    getPermission: function (options = {}) {
        const config = settings(options);
        return wait((finish, cleanup) => {
            const listener = data => finish(!!data.permission);
            FuxEvents.on(NM_GEOLOCATION_PERMISSION, listener);
            cleanup(() => FuxEvents.off(NM_GEOLOCATION_PERMISSION, listener));
            const timer = setTimeout(() => finish(false), config.permissionCheckTimeoutMs);
            cleanup(() => clearTimeout(timer));
            BookizonAppManager.getGeolocationPermission();
        }, false, config.owner);
    },
    askPermission: function () {
        BookizonAppManager.askGeolocationPermission();
    },
    waitForPermissionGrant: function (options = {}) {
        const config = settings(options);
        return wait((finish, cleanup) => {
            let check = null;
            let stopped = false;
            const listener = data => {
                if (data.permission || data.requested) finish(!!data.permission);
            };
            FuxEvents.on(NM_GEOLOCATION_PERMISSION, listener);
            cleanup(() => FuxEvents.off(NM_GEOLOCATION_PERMISSION, listener));
            cleanup(() => {
                stopped = true;
                if (check) check.cancel();
            });
            const poll = () => {
                if (stopped || check) return;
                check = NativeGeolocationProvider.getPermission({...config, owner: null});
                check.then(granted => {
                    check = null;
                    if (!stopped && granted) finish(true);
                });
            };
            const interval = setInterval(poll, config.permissionPollIntervalMs);
            const timeout = setTimeout(() => finish(false), config.permissionTimeoutMs);
            cleanup(() => clearInterval(interval));
            cleanup(() => clearTimeout(timeout));
            if (config.requestPermission) NativeGeolocationProvider.askPermission();
            // Polling is retained for old bridges that don't send a result after Settings changes.
            if (!stopped) poll();
        }, false, config.owner);
    },
    waitForPosition: function (initiator, fallbackToLastLocation = true, options = {}) {
        const config = settings(options);
        return wait((finish, cleanup) => {
            const start = !activePosition;
            const request = activePosition || newPositionRequest();
            const subscriber = {finish, options: config, fallback: fallbackToLastLocation};
            request.subscribers.add(subscriber);
            cleanup(() => {
                request.subscribers.delete(subscriber);
                if (!request.subscribers.size) stopPosition(request);
            });
            const timer = setTimeout(() => finish(fallbackToLastLocation ? cachedLocation(config, !request.modern) : null), config.timeoutMs);
            cleanup(() => clearTimeout(timer));
            if (fallbackToLastLocation && !request.modern) {
                // Legacy iOS may not emit another fix while stationary. Give new events precedence.
                const fallbackTimer = setTimeout(() => {
                    const location = bufferedLegacyLocation(config);
                    if (location) finish(location);
                }, Math.min(config.legacyFallbackDelayMs, config.timeoutMs));
                cleanup(() => clearTimeout(fallbackTimer));
            }
            if (start) {
                try {
                    BookizonAppManager.requestGeolocation(request.id, config.nativeTimeoutMs);
                } catch (error) {
                    request.onError({requestId: request.modern ? request.id : null, code: 'POSITION_UNAVAILABLE'});
                }
            }
        }, null, config.owner);
    },
    getCurrentLocation: function (initiator, fallbackToLastLocation = true, askPermissionIfNeeded = true, options = {}) {
        const config = settings(options);
        const innerOptions = {...config, owner: null};
        let cancelled = false;
        let pending = NativeGeolocationProvider.getPermission(innerOptions);
        const failure = code => Object.assign(new Error("Non è stato possibile recuperare la posizione attuale. Controllare i permessi di geolocalizzazione dell'app e riprova."), {code});
        const result = pending.then(permission => {
            if (cancelled) throw failure('CANCELLED');
            if (permission) return true;
            if (!askPermissionIfNeeded) throw failure('PERMISSION_DENIED');
            pending = NativeGeolocationProvider.waitForPermissionGrant({...innerOptions, requestPermission: true});
            return pending;
        }).then(granted => {
            if (cancelled) throw failure('CANCELLED');
            if (!granted) throw failure('PERMISSION_DENIED');
            pending = NativeGeolocationProvider.waitForPosition(initiator, fallbackToLastLocation, innerOptions);
            return pending;
        }).then(location => {
            if (cancelled) throw failure('CANCELLED');
            if (!location) throw failure('POSITION_UNAVAILABLE');
            return {
                latitude: location.lat,
                longitude: location.lng,
                accuracy: location.accuracy,
                timestamp: location.timestamp,
                receivedAt: location.receivedAt,
                fromCache: !!location.fromCache
            };
        });
        result.cancel = () => {
            cancelled = true;
            pending.cancel();
        };
        return owned(result, config.owner);
    },
    cancelForOwner: function (owner) {
        const requests = ownerRequests.get(owner);
        if (requests) [...requests].forEach(request => request.cancel());
    }
};

export default NativeGeolocationProvider;
